import { SKILL_LABELS, type Character, type Skill } from '@/entities/character/types/character'
import { type Background, type ClassChoice, type ClassChoiceOption, type DndClass, type Feat, type LevelChoice, type Species } from '@/entities/content'

/** A choice's cap at a character level: its `countByLevel` total, else `choose`. */
export function chooseCountAt(choice: ClassChoice, level: number): number {
  return choice.countByLevel?.[Math.min(Math.max(level, 1), 20) - 1] ?? choice.choose
}

/**
 * The class choices (incl. species ones) that are unlocked at `level`. Given the
 * character's `classChoices`, a choice that `requires` another choice's option is
 * only unlocked once that option is held. `classId`, when given, namespaces the
 * `requires.choice` lookup the same way `classChoiceKey` namespaces every other
 * class-scoped read (Ranger's Druidic Warrior needs its class's own Fighting
 * Style pick first); omitted, the lookup stays bare (species choices, or a caller
 * that hasn't been updated to pass it yet).
 */
export function unlockedChoices(
  choices: ClassChoice[] | undefined,
  level: number,
  classChoices?: Record<string, string[]>,
  classId?: string,
): ClassChoice[] {
  return (choices ?? []).filter((c) => {
    if ((c.minLevel ?? 1) > level) return false
    if (!c.requires || !classChoices) return true
    const key = classId ? `${classId}#${c.requires.choice}` : c.requires.choice
    // A pick may still sit under the bare id: the Background step presets an origin feat's choice
    // there (Acolyte: Magic Initiate's list is Cleric), and so did saves from before keys were per
    // class. The class's own key wins when it exists.
    return (classChoices[key] ?? classChoices[c.requires.choice] ?? []).includes(c.requires.option)
  })
}

/**
 * The species choices a character is offered: those for their lineage (`subspecies`), and a
 * choice that `requires` another's option only once that option is held (Kobold Legacy's
 * Craftiness opens its skill pick). Picks left behind by a changed option don't count.
 */
export function speciesChoices(species: Species | undefined, character: Pick<Character, 'subspeciesId' | 'classChoices'>): ClassChoice[] {
  return (species?.choices ?? []).filter(
    (c) =>
      (!c.subspecies || c.subspecies.includes(character.subspeciesId ?? '')) &&
      (!c.requires || (character.classChoices[c.requires.choice] ?? []).includes(c.requires.option)),
  )
}

/**
 * The subset of `ClassChoice`/`LevelChoice` these key functions read — a choice
 * from either a class's standing `levelOneChoices` (`ClassChoice`, no `ref`) or
 * a level-up's `choices` (`LevelChoice`) can produce a storage key.
 */
type KeyableChoice = { id?: string; ref?: string; type: string }

/**
 * Storage key in `Character.classChoices` for a level-up choice. Explicit `id`
 * wins, then `ref` (so a re-opened class choice pools with the original), then
 * a per-type legacy default so pre-existing packs and saved characters keep
 * their accumulated picks.
 */
export function levelChoiceKey(choice: KeyableChoice): string {
  if (choice.id) return choice.id
  if (choice.ref) return choice.ref
  if (choice.type === 'expertise') return 'expertise'
  if (choice.type === 'invocations') return 'eldritch-invocations'
  if (choice.type === 'subclass') return 'subclass'
  return choice.type
}

/**
 * Storage key in `Character.classChoices` for a class-scoped choice, always
 * namespaced by the owning class so two held classes can never collide on the
 * same default key (every class's subclass pick used to share the literal
 * `'subclass'`). Explicit `id` wins, then `ref`, then a per-type legacy
 * default, matching the pre-namespacing key `levelChoiceKey` used to produce.
 */
export function classChoiceKey(classId: string, choice: KeyableChoice): string {
  return `${classId}#${levelChoiceKey(choice)}`
}

/** Storage key of a class's subclass pick: namespaced like every class-scoped choice, so each held class keeps its own. */
export function subclassKey(classId: string): string {
  return classChoiceKey(classId, { type: 'subclass' })
}

/**
 * The subclass option `cls` has chosen, if any. Read under the class's own key, else under
 * the bare `'subclass'` a save written before the key was namespaced holds: a bare pick only
 * counts for the class that actually offers that option, so it never reaches another class.
 */
export function chosenSubclass(cls: DndClass, classChoices: Record<string, string[]>): ClassChoiceOption | undefined {
  const chosen = (classChoices[subclassKey(cls.id)] ?? classChoices['subclass'])?.[0]
  return cls.levels?.flatMap((l) => l.choices).find((c) => c.type === 'subclass')?.options?.find((o) => o.id === chosen)
}

/**
 * The class as it stands for a character: when `classChoices` names a subclass, that
 * option's `levels` are merged into the class's (features and choices appended per
 * level) and its `levelOneChoices` appended to the class's. Everything that reads a
 * character's class should read this, so subclass content only reaches the
 * characters who chose that subclass.
 */
export function withSubclass<C extends DndClass | undefined>(cls: C, classChoices: Record<string, string[]>): C {
  if (!cls) return cls
  const sub = chosenSubclass(cls, classChoices)
  const casterFields = sub && (sub.casterType || sub.cantripsByLevel || sub.preparedSpellsByLevel || sub.spellSlots || sub.spellcastingAbility)
    ? {
        ...(sub.casterType && { casterType: sub.casterType }),
        ...(sub.spellcastingAbility && { spellcastingAbility: sub.spellcastingAbility }),
        ...(sub.cantripsByLevel && { cantripsByLevel: sub.cantripsByLevel }),
        ...(sub.preparedSpellsByLevel && { preparedSpellsByLevel: sub.preparedSpellsByLevel }),
        ...(sub.spellSlots && { spellSlots: sub.spellSlots }),
      }
    : undefined
  if (!sub?.levels && !sub?.levelOneChoices && !casterFields) return cls
  const levels = (cls.levels ?? []).map((l) => {
    const extra = sub?.levels?.find((s) => s.level === l.level)
    return extra ? { ...l, features: [...l.features, ...extra.features], choices: [...l.choices, ...(extra.choices ?? [])] } : l
  })
  for (const s of sub?.levels ?? [])
    if (!levels.some((l) => l.level === s.level)) levels.push({ level: s.level, features: s.features, choices: s.choices ?? [] })
  levels.sort((a, b) => a.level - b.level)
  return { ...cls, ...casterFields, levels, levelOneChoices: [...(cls.levelOneChoices ?? []), ...(sub?.levelOneChoices ?? [])] }
}

/** The class level at which `cls` chooses its subclass, from its level data. */
export function subclassChoiceLevel(cls: DndClass | undefined): number | undefined {
  return cls?.levels?.find((l) => l.choices.some((c) => c.type === 'subclass'))?.level
}

/**
 * Every choice definition that can hold selections for this character: the
 * species' choices (keyed by their own id) and, for every held class, its
 * standing choices and the per-level choices up to that class's own level
 * (keyed by `classChoiceKey`, namespaced to the owning class).
 */
export interface ChoiceDef {
  key: string
  type: ClassChoice['type'] | LevelChoice['type']
  featCategory?: ClassChoice['featCategory']
}

export function choiceDefs(species: Species | undefined, held: { cls: DndClass; level: number }[]): ChoiceDef[] {
  const defs: ChoiceDef[] = []
  for (const c of species?.choices ?? []) defs.push({ key: c.id, type: c.type, featCategory: c.featCategory })
  for (const { cls, level } of held) {
    for (const c of cls.levelOneChoices ?? []) defs.push({ key: classChoiceKey(cls.id, c), type: c.type, featCategory: c.featCategory })
    for (const l of cls.levels ?? []) {
      if (l.level > level) continue
      for (const c of l.choices) defs.push({ key: classChoiceKey(cls.id, c), type: c.type, featCategory: c.featCategory })
    }
  }
  return defs
}

/** Selections stored for choice defs matching `types`, de-duplicated. */
function selectionsOfType(
  character: Character,
  defs: ChoiceDef[],
  types: ChoiceDef['type'][],
): string[] {
  const out = new Set<string>()
  for (const def of defs) {
    if (!types.includes(def.type)) continue
    for (const id of character.classChoices[def.key] ?? []) out.add(id)
  }
  return [...out]
}

/**
 * Where each of the character's skill proficiencies came from, keyed by skill:
 * the background, the class's own skill picks, or a named skill-type choice
 * (species traits like Keen Senses, class features like Primal Knowledge).
 *
 * The label is meant to be read back to the player — "Elf: Keen Senses" — so a
 * picker can show a skill they already have and say why it isn't on offer.
 * First source wins, so the most specific grant keeps the credit.
 */
export function skillSources(
  character: Character,
  content: { classes: DndClass[]; species: Species[]; backgrounds: Background[]; feats: Feat[] },
): Partial<Record<Skill, string>> {
  const species = content.species.find((s) => s.id === character.speciesId)
  const bg = content.backgrounds.find((b) => b.id === character.backgroundId)
  const held = resolvedClasses(character, content)
  const sources: Partial<Record<Skill, string>> = {}
  // Only skills: a skill-type choice may also hold tool picks (Skilled).
  const claim = (skill: Skill, from: string) => {
    if (skill in SKILL_LABELS && !sources[skill]) sources[skill] = from
  }

  for (const skill of bg?.skillProficiencies ?? []) claim(skill, bg!.name)

  // The class's own skill picks, read from that class's own bucket — so a second
  // held class's picks are credited to it, not swallowed by the first class's claim.
  // A saved character whose classId no longer resolves (content missing) still gets
  // these picks credited, just without a class to name.
  if (held.length) {
    for (const { cls } of held) for (const skill of character.skillProficiencies[cls.id] ?? []) claim(skill, cls.name)
  } else {
    for (const skill of Object.values(character.skillProficiencies).flat()) claim(skill, 'your class')
  }

  // Named skill-type choices, so the trait that granted it can be credited.
  for (const choice of speciesChoices(species, character))
    if (choice.type === 'skill')
      for (const id of character.classChoices[choice.id] ?? [])
        claim(id as Skill, `${species!.name}: ${choice.name}`)

  for (const { cls, level } of held) {
    for (const choice of cls.levelOneChoices ?? [])
      if (choice.type === 'skill')
        for (const id of character.classChoices[classChoiceKey(cls.id, choice)] ?? [])
          claim(id as Skill, `${cls.name}: ${choice.name}`)

    for (const lvl of cls.levels ?? []) {
      if (lvl.level > level) continue
      for (const choice of lvl.choices) {
        if (choice.type !== 'skill') continue
        for (const id of character.classChoices[classChoiceKey(cls.id, choice)] ?? [])
          claim(id as Skill, `${cls.name} level ${lvl.level}`)
      }
    }
  }

  return sources
}

/**
 * All skill proficiencies the character holds: directly chosen class skills,
 * the background's, and any granted through skill-type choices (species
 * traits like Keen Senses, class features like Primal Knowledge).
 */
export function allSkillProficiencies(
  character: Character,
  content: { classes: DndClass[]; species: Species[]; backgrounds: Background[]; feats: Feat[] },
): Skill[] {
  return Object.keys(skillSources(character, content)) as Skill[]
}

/**
 * Picks stored for expertise-type choices (the Rogue's and Bard's Expertise,
 * at level 1 and on later level-ups). Mostly skill ids; a pick may also name a
 * tool (Thieves' Tools).
 */
export function expertisePicks(
  character: Character,
  content: { classes: DndClass[]; species: Species[]; backgrounds: Background[]; feats: Feat[] },
): string[] {
  const species = content.species.find((s) => s.id === character.speciesId)
  const held = resolvedClasses(character, content)
  return selectionsOfType(character, choiceDefs(species, held), ['expertise'])
}

/**
 * Every feat the character holds: the background's origin feat, feats picked
 * on level-ups (`featIds`), and feats granted through feat- or
 * fighting-style-type choices (species Versatile, class Fighting Style).
 */
export function allFeatIds(
  character: Character,
  content: { classes: DndClass[]; species: Species[]; backgrounds: Background[]; feats: Feat[] },
): string[] {
  const species = content.species.find((s) => s.id === character.speciesId)
  const bg = content.backgrounds.find((b) => b.id === character.backgroundId)
  const held = resolvedClasses(character, content)
  const fromChoices = selectionsOfType(character, choiceDefs(species, held), [
    'feat',
    'fightingStyle',
  ])
  return [...new Set([...(bg ? [bg.originFeat] : []), ...character.featIds, ...fromChoices])]
}

/** Tools picked through skill-type choices that offer tools (Skilled), by name. */
export function toolPicks(character: Character, cls: DndClass | undefined, level: number): string[] {
  return unlockedChoices(cls?.levelOneChoices, level, character.classChoices)
    .filter((c) => c.type === 'skill' && c.includeTools)
    .flatMap((c) => (character.classChoices[cls ? classChoiceKey(cls.id, c) : c.id] ?? []).filter((id) => !(id in SKILL_LABELS)))
}

/** Each feat a character holds, once per copy (a Repeatable feat taken twice is listed twice), and whether a level-up gave it. */
function featCopies(character: Character, species: Species | undefined, held: { cls: DndClass; level: number }[], bg: Background | undefined, feats: Feat[]) {
  const fromChoices = selectionsOfType(character, choiceDefs(species, held), ['feat', 'fightingStyle'])
  const ids = [
    ...(bg ? [{ id: bg.originFeat, levelUp: false, origin: true }] : []),
    ...character.featIds.map((id) => ({ id, levelUp: true, origin: false })),
    ...fromChoices.map((id) => ({ id, levelUp: false, origin: false })),
  ]
  const seen = new Set<string>()
  return ids.flatMap(({ id, levelUp, origin }) => {
    const feat = feats.find((f) => f.id === id)
    // Only a Repeatable feat counts twice; anything else held twice is the same feat.
    if (!feat || (seen.has(id) && !feat.repeatable)) return []
    seen.add(id)
    return [{ feat, levelUp, origin }]
  })
}

/** The feats a character holds, as content, once per copy (see {@link allFeatIds}), across every held class. */
export function heldFeats(
  character: Character,
  content: { classes: DndClass[]; species: Species[]; backgrounds: Background[]; feats: Feat[] },
): Feat[] {
  const species = content.species.find((s) => s.id === character.speciesId)
  const bg = content.backgrounds.find((b) => b.id === character.backgroundId)
  const held = resolvedClasses(character, content)
  return featCopies(character, species, held, bg, content.feats).map((c) => c.feat)
}

/**
 * The choices of every feat copy a character holds, for `cls`'s own resolution
 * ({@link resolvedClass} appends the result to that one class's `levelOneChoices`).
 * A second copy's choices are stored under `<id>#2` (its `requires` and
 * `abilityFrom` follow), and a `distinctPerRepeat` choice drops the options
 * other copies picked. A background that presets its origin feat's choices
 * (`originFeatChoices`) narrows those to the preset options; one left, and
 * the description says the background decided it.
 */
export function featChoices(
  character: Character, species: Species | undefined, cls: DndClass | undefined, bg: Background | undefined, feats: Feat[],
): { choice: ClassChoice; levelUp: boolean }[] {
  const ownLevel = character.classes.find((c) => c.classId === cls?.id)?.level ?? 1
  const held = featCopies(character, species, cls ? [{ cls, level: ownLevel }] : [], bg, feats)
  const copies: Record<string, number> = {}
  const suffix = (id: string, n: number) => (n > 1 ? `${id}#${n}` : id)
  return held.flatMap(({ feat, levelUp, origin }) => {
    const n = (copies[feat.id] = (copies[feat.id] ?? 0) + 1)
    const total = held.filter((h) => h.feat.id === feat.id).length
    return (feat.choices ?? []).map((c) => {
      const preset = origin ? bg?.originFeatChoices?.[c.id] : undefined
      // The options the feat's other copies picked for this choice.
      const others = c.distinctPerRepeat
        ? Array.from({ length: total }, (_, k) => k + 1).filter((k) => k !== n).flatMap((k) => character.classChoices[suffix(c.id, k)] ?? [])
        : []
      const options = c.options?.filter((o) => (!preset || preset.includes(o.id)) && !others.includes(o.id))
      // A background that leaves one option decides the choice: say so instead of asking.
      const decided = preset && options?.length === 1 ? options[0] : undefined
      const choice: ClassChoice = {
        ...c,
        id: suffix(c.id, n),
        name: n > 1 ? `${c.name} (${n})` : c.name,
        ...(decided ? { description: `${bg!.name} grants ${feat.name} (${decided.name}).` } : {}),
        ...(c.options ? { options } : {}),
        ...(c.requires ? { requires: { ...c.requires, choice: suffix(c.requires.choice, n) } } : {}),
        ...(c.spellOptions?.abilityFrom ? { spellOptions: { ...c.spellOptions, abilityFrom: suffix(c.spellOptions.abilityFrom, n) } } : {}),
      }
      return { choice, levelUp }
    })
  })
}

/**
 * The class as it stands for a character, with the chosen subclass ({@link withSubclass})
 * and the `choices` of every feat they hold appended to its `levelOneChoices`, so feat
 * picks (Skilled's skills, Magic Initiate's spells, a Boon's ability) are read like any
 * standing choice. A background that presets its origin feat's choices
 * (`originFeatChoices`) narrows those choices to the preset options. Resolves
 * ONE held class by id — a multiclass character calls this once per entry in `classes[]`.
 */
export function resolvedClass(
  character: Character,
  classId: string,
  content: { classes: DndClass[]; species: Species[]; backgrounds: Background[]; feats: Feat[] },
): DndClass | undefined {
  if (!character.classes.some((c) => c.classId === classId)) return undefined
  const cls = withSubclass(content.classes.find((c) => c.id === classId), character.classChoices)
  if (!cls) return cls
  const species = content.species.find((s) => s.id === character.speciesId)
  const bg = content.backgrounds.find((b) => b.id === character.backgroundId)
  const choices = featChoices(character, species, cls, bg, content.feats).map((c) => c.choice)
  return choices.length ? { ...cls, levelOneChoices: [...(cls.levelOneChoices ?? []), ...choices] } : cls
}

/** Every held class, resolved (subclass + feat choices merged in), in `classes[]` order — a classId the loaded content no longer has is skipped. */
export function resolvedClasses(
  character: Character,
  content: { classes: DndClass[]; species: Species[]; backgrounds: Background[]; feats: Feat[] },
): { cls: DndClass; level: number }[] {
  return character.classes.flatMap(({ classId, level }) => {
    const cls = resolvedClass(character, classId, content)
    return cls ? [{ cls, level }] : []
  })
}

/**
 * The character with every class-scoped pick under its class's own key. A save from before
 * keys were namespaced (or a pick the Feats step wrote bare) holds them under the choice's
 * bare id; each moves to the first held class that declares the choice and has no pick of
 * its own yet. Needs content, so it isn't a numbered migration: `upgradeStoredIds` saves it
 * and `levelUpPlan` reads through it. Returns the same object when nothing moved.
 */
export function namespaceClassChoices(
  character: Character,
  content: { classes: DndClass[]; species: Species[]; backgrounds: Background[]; feats: Feat[] },
): Character {
  const moved = { ...character.classChoices }
  let changed = false
  for (const { cls } of resolvedClasses(character, content)) {
    const keys = new Set([...(cls.levelOneChoices ?? []), ...(cls.levels ?? []).flatMap((l) => l.choices)].map(levelChoiceKey))
    for (const key of keys) {
      const own = `${cls.id}#${key}`
      if (moved[key] === undefined || moved[own] !== undefined) continue
      // A bare subclass pick belongs to the class that offers that subclass.
      if (key === 'subclass' && !chosenSubclass(cls, { subclass: moved[key] })) continue
      moved[own] = moved[key]
      delete moved[key]
      changed = true
    }
  }
  return changed ? { ...character, classChoices: moved } : character
}

/**
 * What a class grants toward armor/weapon/tool proficiencies and skill choices
 * — its full starting set as the character's first class, or its reduced
 * `multiclassProficiencies` for any class after (SRD 5.2.1 Multiclassing,
 * "Proficiencies": "you gain only some of the new class's starting
 * proficiencies").
 */
export function multiclassProficiencyGrant(
  cls: DndClass,
  isFirstClass: boolean,
): { armorProficiencies: string[]; weaponProficiencies: string[]; toolProficiencies: string[]; skillChoices: { count: number; options: Skill[] } } {
  if (isFirstClass) {
    return {
      armorProficiencies: cls.armorProficiencies,
      weaponProficiencies: cls.weaponProficiencies,
      toolProficiencies: cls.toolProficiencies ?? [],
      skillChoices: cls.skillChoices,
    }
  }
  const mc = cls.multiclassProficiencies
  return {
    armorProficiencies: mc?.armorProficiencies ?? [],
    weaponProficiencies: mc?.weaponProficiencies ?? [],
    toolProficiencies: mc?.toolProficiencies ?? [],
    skillChoices: mc?.skillChoices ?? { count: 0, options: [] },
  }
}

/**
 * The Ability Score Improvements a character's class levels have granted (the `asiOrFeat` rows of
 * each held class's table, up to that class's own level) and how many are spent: two points of
 * `levelBonuses`, or one General feat among `featIds`, each. The level-up screen hands them
 * out one by one; the wizard uses this for a character created above level 1.
 */
export function abilityImprovements(
  character: Character,
  content: { classes: DndClass[]; species: Species[]; backgrounds: Background[]; feats: Feat[] },
): { granted: number; spent: number } {
  const rows = resolvedClasses(character, content).flatMap(({ cls, level }) => (cls.levels ?? []).filter((l) => l.level <= level).flatMap((l) => l.choices))
  const granted = rows.filter((c) => c.type === 'asiOrFeat').length
  const points = Object.values(character.levelBonuses ?? {}).reduce<number>((n, v) => n + (v ?? 0), 0)
  const held = (category: Feat['category']) => character.featIds.filter((id) => content.feats.find((f) => f.id === id)?.category === category).length
  // Level 19's feat ("an Epic Boon feat or another feat of your choice") may hold a General feat
  // too: those it has room for, beyond the Epic Boons held, are no improvement spent.
  const inBoonSlots = Math.max(0, Math.min(held('General'), rows.filter((c) => c.type === 'feat').length - held('Epic Boon')))
  return { granted, spent: Math.floor(points / 2) + held('General') - inBoonSlots }
}

/**
 * What the levels from 2 up to each class's own level hand out through a per-level choice that
 * adds picks (a Paladin's level 2 Fighting Style, the Rogue's two more Expertise picks at 6), by
 * storage key. The level-up screen asks for them as the level comes; the wizard, for a character
 * created above level 1, adds them to the standing choice of the same key or asks for them as one.
 */
export function levelPicksGranted(cls: DndClass, level: number): { key: string; type: 'expertise' | 'skill' | 'fightingStyle'; count: number; choice: LevelChoice }[] {
  const out: { key: string; type: 'expertise' | 'skill' | 'fightingStyle'; count: number; choice: LevelChoice }[] = []
  for (const l of cls.levels ?? []) {
    if (l.level > level) continue
    for (const c of l.choices) {
      if (c.type !== 'expertise' && c.type !== 'skill' && c.type !== 'fightingStyle') continue
      const key = levelChoiceKey(c)
      const held = out.find((o) => o.key === key)
      if (held) held.count += c.count ?? 1
      else out.push({ key, type: c.type, count: c.count ?? 1, choice: c })
    }
  }
  return out
}
